import { useState, useMemo, useEffect } from 'react'
import Header from '@/components/Header'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { apiFetch } from '@/utils'
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, Bell, ShieldCheck, MoreHorizontal, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import useSWR from 'swr'
import { UserRole } from '@/constants'
import ConfirmModal from '@/components/common/ConfirmationModal'

interface AdminUser {
	id: string
	email: string
	displayName: string | null
	walletAddress: string | null
	role: UserRole
	createdAt: string
	lastLoginAt: string | null
	emailVerified: boolean
}

interface AdminUsersResponse {
	data: AdminUser[]
	meta: {
		currentPage: number
		pageSize: number
		total: number
	}
}

type SortField = 'email' | 'displayName' | 'createdAt' | 'lastLoginAt' | 'role'
type SortOrder = 'asc' | 'desc'

function SortIcon({ field, sortField, sortOrder }: { field: SortField; sortField: SortField; sortOrder: SortOrder }) {
	if (sortField !== field) {
		return <ChevronsUpDown className="h-4 w-4 inline ml-1 opacity-50" />
	}
	return sortOrder === 'asc'
		? <ChevronUp className="h-4 w-4 inline ml-1" />
		: <ChevronDown className="h-4 w-4 inline ml-1" />
}

function formatDate(dateString: string | null) {
	if (!dateString) return 'N/A'
	return new Date(dateString).toLocaleDateString()
}

function formatWalletAddress(address: string | null) {
	if (!address) return 'Not connected'
	return `${address.slice(0, 6)}...${address.slice(-4)}`
}

export default function UsersPage() {
	const [search, setSearch] = useState('')
	const [sortField, setSortField] = useState<SortField>('createdAt')
	const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
	const [sendingNotification, setSendingNotification] = useState(false)
	const [verifyingUser, setVerifyingUser] = useState<AdminUser | null>(null)
	const [verifyingUserId, setVerifyingUserId] = useState<string | null>(null)
	const { toast } = useToast()
	const { uid, id } = useUser()
	const userId = uid || id

	const { data: usersResponse, error: usersError, mutate } = useSWR<AdminUsersResponse>(
		'/v1/admin/users',
		async () => {
			const params = new URLSearchParams()
			if (search) {
				params.append('search', search)
			}
			params.append('pageSize', '500')

			const qs = params.toString()

			const response = await apiFetch(`/v1/admin/users?${qs}`, {
				withCredentials: true
			})

			return response.json()
		}
	)

	// Refetch when search changes
	useEffect(() => {
		mutate()
	}, [search, mutate])

	// Sort data locally
	const usersData = usersResponse?.data
	const sortedUsers = useMemo(() => {
		if (!usersData) return []

		const sorted = [...usersData].sort((a, b) => {
			let aValue: string | null = null
			let bValue: string | null = null

			switch (sortField) {
				case 'email':
					aValue = a.email || ''
					bValue = b.email || ''
					break
				case 'displayName':
					aValue = a.displayName || ''
					bValue = b.displayName || ''
					break
				case 'createdAt':
					aValue = a.createdAt || ''
					bValue = b.createdAt || ''
					break
				case 'lastLoginAt':
					aValue = a.lastLoginAt || ''
					bValue = b.lastLoginAt || ''
					break
				case 'role':
					aValue = a.role || ''
					bValue = b.role || ''
					break
			}

			const comparison = String(aValue).localeCompare(String(bValue))
			return sortOrder === 'asc' ? comparison : -comparison
		})

		return sorted
	}, [usersData, sortField, sortOrder])

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
		} else {
			setSortField(field)
			setSortOrder('asc')
		}
	}

	const handleSendTestNotification = async () => {
		if (!userId) {
			toast({
				title: 'Error',
				description: 'User ID not available',
				variant: 'destructive'
			})
			return
		}

		setSendingNotification(true)
		try {
			const response = await apiFetch('/v1/notifications', {
				method: 'POST',
				withCredentials: true,
				body: JSON.stringify({
					userId,
					title: 'Test Notification',
					message: 'This is a test notification from the admin panel.',
					type: 'alert'
				})
			})

			if (!response.ok) {
				throw new Error('Failed to send notification')
			}

			toast({
				title: 'Test notification sent',
				description: 'Check your notification bell for the test message.'
			})
		} catch (error) {
			toast({
				title: 'Failed to send notification',
				description: error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive'
			})
		} finally {
			setSendingNotification(false)
		}
	}

	const handleVerifyUser = async () => {
		if (!verifyingUser) return

		setVerifyingUserId(verifyingUser.id)
		setVerifyingUser(null)
		try {
			const response = await apiFetch(`/v1/admin/users/${verifyingUser.id}/verify`, {
				method: 'PATCH',
				withCredentials: true
			})

			if (!response.ok) {
				throw new Error('Failed to verify user')
			}

			toast({
				title: 'User verified',
				description: `${verifyingUser.email} has been manually verified.`
			})

			mutate()
		} catch (error) {
			toast({
				title: 'Failed to verify user',
				description: error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive'
			})
		} finally {
			setVerifyingUserId(null)
		}
	}

	return (
		<>
			<Header />
			<div className="max-w-5xl mx-auto px-5 pb-8">
				<div className="flex justify-between items-center mb-6">
					<h1 className="text-3xl font-bold">Users</h1>
					<Button
						variant="outline"
						onClick={handleSendTestNotification}
						disabled={sendingNotification}
					>
						<Bell className="h-4 w-4 mr-2" />
						{sendingNotification ? 'Sending...' : 'Test Notification'}
					</Button>
				</div>

				<Card className="p-4 mb-6">
					<div className="flex items-center gap-2">
						<Search className="h-5 w-5 text-muted-foreground shrink-0" />
						<Input
							placeholder="Search by email, name, or wallet address..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="flex-1"
						/>
					</div>
				</Card>

				<Card className="p-6">
					<h2 className="text-xl font-semibold mb-4">
						Users List
						{usersResponse?.meta?.total !== undefined && (
							<span className="text-muted-foreground text-sm font-normal ml-2">
								({usersResponse.meta.total} total)
							</span>
						)}
					</h2>

					{usersError ? (
						<div className="text-destructive">Failed to load users</div>
					) : !usersResponse ? (
						<div className="text-muted-foreground">Loading users...</div>
					) : !sortedUsers?.length ? (
						<div className="text-muted-foreground">No users found</div>
					) : (
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead
											className="cursor-pointer hover:bg-muted/50 select-none"
											onClick={() => handleSort('email')}
										>
											Email <SortIcon field="email" sortField={sortField} sortOrder={sortOrder} />
										</TableHead>
										<TableHead
											className="cursor-pointer hover:bg-muted/50 select-none"
											onClick={() => handleSort('displayName')}
										>
											Name <SortIcon field="displayName" sortField={sortField} sortOrder={sortOrder} />
										</TableHead>
										<TableHead>Wallet</TableHead>
										<TableHead
											className="cursor-pointer hover:bg-muted/50 select-none"
											onClick={() => handleSort('role')}
										>
											Role <SortIcon field="role" sortField={sortField} sortOrder={sortOrder} />
										</TableHead>
										<TableHead
											className="cursor-pointer hover:bg-muted/50 select-none"
											onClick={() => handleSort('createdAt')}
										>
											Joined <SortIcon field="createdAt" sortField={sortField} sortOrder={sortOrder} />
										</TableHead>
										<TableHead
											className="cursor-pointer hover:bg-muted/50 select-none"
											onClick={() => handleSort('lastLoginAt')}
										>
											Last Login <SortIcon field="lastLoginAt" sortField={sortField} sortOrder={sortOrder} />
										</TableHead>
										<TableHead>Verified</TableHead>
										<TableHead>Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{sortedUsers.map((user) => (
										<TableRow key={user.id}>
											<TableCell className="font-medium">{user.email}</TableCell>
											<TableCell>{user.displayName || 'N/A'}</TableCell>
											<TableCell className="font-mono text-sm">
												{formatWalletAddress(user.walletAddress)}
											</TableCell>
											<TableCell>
												<Badge variant={user.role === UserRole.ADMIN ? 'default' : 'secondary'}>
													{user.role}
												</Badge>
											</TableCell>
											<TableCell>{formatDate(user.createdAt)}</TableCell>
											<TableCell>{formatDate(user.lastLoginAt)}</TableCell>
											<TableCell>
												<Badge variant={user.emailVerified ? 'default' : 'outline'}>
													{user.emailVerified ? 'Yes' : 'No'}
												</Badge>
											</TableCell>
											<TableCell>
												{verifyingUserId === user.id ? (
													<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
												) : (
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
																<MoreHorizontal className="h-4 w-4" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuLabel>Actions</DropdownMenuLabel>
															<DropdownMenuItem
																disabled={user.emailVerified}
																onClick={() => setVerifyingUser(user)}
															>
																<ShieldCheck className="h-4 w-4 mr-2" />
																Verify Email
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</Card>
			</div>

			<ConfirmModal
				open={!!verifyingUser}
				onClose={() => setVerifyingUser(null)}
				onConfirm={handleVerifyUser}
				title="Verify User"
				description={`Are you sure you want to manually verify ${verifyingUser?.email}? This will mark their email as verified.`}
				confirmText="Verify"
				cancelText="Cancel"
			/>
		</>
	)
}
